import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Music, Play, Pause, Upload, Trash2, Lock, ArrowRight,
  Loader2, ShoppingCart, Volume2, Sparkles, SkipForward,
  Clock, Headphones, X, Check, Edit3, Save, Image,
  Disc3, ChevronDown, ChevronUp, Link2, Search,
  AlertTriangle, SkipBack, Radio, Info, GripVertical, Download
} from 'lucide-react';
import PayPalCheckoutModal from './PayPalCheckoutModal';
import useInlineAdmin from '../hooks/useInlineAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/* ─────────── Square Track Card ─────────── */
function TrackCard({ track, isPlaying, onTogglePlay, onEnded, trackNumber, onError, hasError, onShare, isAdmin, onEdit, onDelete, onDownload, deleting, onShowCredits, onDragStart, onDragOver, onDrop, isDragging, useGlobalPlayer, onBuy }) {
  const audioRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [audioError, setAudioError] = useState(false);

  useEffect(() => {
    // Skip local audio if using global player
    if (useGlobalPlayer) return;
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying && !audioError) {
      setLoading(true);
      audio.play().then(() => {
        setLoading(false);
        try {
          window.dispatchEvent(new CustomEvent('kazajah:audio-playing'));
          // Ping play counter (idempotent +1 per actual playback start)
          fetch(`${API}/playlist/tracks/${track.id}/play`, { method: 'POST' }).catch(() => {});
        } catch {}
      }).catch((err) => {
        setLoading(false);
        if (err.name === 'NotAllowedError') return;
        setAudioError(true);
        onError?.(track.id);
      });
    } else {
      audio.pause();
    }
  }, [isPlaying, audioError, track.id, onError, useGlobalPlayer]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setProgress(audioRef.current.currentTime);
      setDuration(audioRef.current.duration || 0);
    }
  };

  // When the track is being played by the GLOBAL mini-player (not our local
  // <audio>), listen for the broadcast progress events from App.js.
  useEffect(() => {
    if (!useGlobalPlayer || !isPlaying) return;
    const onProgress = (e) => {
      if (!e.detail || e.detail.id !== track.id) return;
      setProgress(e.detail.currentTime || 0);
      if (e.detail.duration) setDuration(e.detail.duration);
    };
    window.addEventListener('kazajah:audio-progress', onProgress);
    return () => window.removeEventListener('kazajah:audio-progress', onProgress);
  }, [useGlobalPlayer, isPlaying, track.id]);
  const handleEnded = () => { setProgress(0); onEnded(); };
  const handleAudioError = () => {
    const audio = audioRef.current;
    if (audio && audio.networkState === 3) {
      setAudioError(true); setLoading(false); onError?.(track.id);
      if (isPlaying) onEnded();
    }
  };
  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };
  const seekTo = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (audioRef.current && duration) audioRef.current.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  const audioSrc = `${API}/playlist/tracks/${track.id}/audio`;
  const DEFAULT_LOGO_BLACK = 'https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/ce9n02vn_LOGO%20BLACK.jpg';
  const DEFAULT_LOGO_WHITE = 'https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/dtxrk43z_LOGO%20WHITE.jpg';
  const defaultLogo = trackNumber % 2 === 1 ? DEFAULT_LOGO_BLACK : DEFAULT_LOGO_WHITE;
  const [imgSrc, setImgSrc] = useState(track.photo ? (track.photo.startsWith('http') ? track.photo : defaultLogo) : defaultLogo);
  const photoSrc = imgSrc;
  const displayError = audioError || hasError;

  return (
    <motion.div
      data-testid={`playlist-track-${track.id}`} id={`track-${track.id}`}
      layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: isDragging ? 0.5 : 1, scale: 1 }}
      draggable={isAdmin}
      onDragStart={(e) => isAdmin && onDragStart?.(e, track.id)}
      onDragOver={(e) => isAdmin && onDragOver?.(e)}
      onDrop={(e) => isAdmin && onDrop?.(e, track.id)}
      className={`relative aspect-square rounded-2xl overflow-hidden group border-2 transition-all ${
        isAdmin ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
      } ${displayError ? 'border-red-500/50 opacity-50' :
        isPlaying ? 'border-orange-500 shadow-2xl shadow-orange-500/60 ring-4 ring-orange-500/30 scale-[1.02]' :
        'border-zinc-800 hover:border-zinc-600'
      }`}
    >
      <audio ref={audioRef} src={audioSrc} onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleTimeUpdate} onEnded={handleEnded} onError={handleAudioError} preload="none" />

      {/* Background: photo or gradient */}
      {photoSrc ? (
        <img src={photoSrc} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 via-zinc-900 to-black" />
      )}
      <div className={`absolute inset-0 ${isPlaying ? 'bg-black/30' : 'bg-black/45 group-hover:bg-black/35'} transition-all`} />

      {/* Top bar: number + admin buttons */}
      <div className="absolute top-0 left-0 right-0 p-2 flex items-start justify-between z-10">
        <div className="flex items-center gap-1">
          {isAdmin && <GripVertical size={14} className="text-white/40" />}
          {isPlaying ? (
            <span className="px-1.5 py-0.5 rounded-full bg-orange-500 text-white text-[8px] font-black uppercase tracking-widest animate-pulse flex items-center gap-1">
              <Volume2 size={8} /> En cours
            </span>
          ) : (
            <span className="text-xs font-bold text-white/60" data-testid={`track-number-${track.id}`}>#{trackNumber}</span>
          )}
        </div>
        <div className="flex gap-1">
          {isAdmin && (
            <>
              <button onClick={(e) => { e.stopPropagation(); onEdit(track); }} data-testid={`edit-track-${track.id}`}
                className="p-1.5 rounded-lg bg-black/50 text-white/70 hover:bg-orange-500 hover:text-white transition-colors">
                <Edit3 size={11} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); onDownload?.(track); }} data-testid={`download-track-${track.id}`}
                title="Telecharger l'audio (admin)"
                className="p-1.5 rounded-lg bg-black/50 text-white/70 hover:bg-emerald-500 hover:text-white transition-colors">
                <Download size={11} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); onDelete(track.id); }} disabled={deleting} data-testid={`delete-track-${track.id}`}
                className="p-1.5 rounded-lg bg-black/50 text-white/70 hover:bg-red-500 hover:text-white transition-colors disabled:opacity-40">
                {deleting ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Center: Play — proper <button> for reliable click/tap handling
          across all browsers (especially mobile Safari which can miss
          onClick on non-interactive elements like divs). */}
      <button type="button"
        data-testid={`play-track-${track.id}`}
        aria-label={isPlaying ? `Mettre en pause ${track.title}` : `Jouer ${track.title}`}
        onClick={(e) => { e.stopPropagation(); if (!displayError) onTogglePlay(); }}
        className="absolute inset-0 flex items-center justify-center focus:outline-none">
        {displayError ? (
          <div className="w-12 h-12 rounded-full bg-red-500/30 flex items-center justify-center">
            <AlertTriangle size={22} className="text-red-400" />
          </div>
        ) : (
          <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
            isPlaying ? 'bg-orange-500 scale-100' : 'bg-white/15 backdrop-blur-sm scale-90 group-hover:scale-100'
          }`}>
            {loading ? <Loader2 size={22} className="animate-spin text-white" /> :
              isPlaying ? <Pause size={22} className="text-white" /> : <Play size={22} className="text-white ml-0.5" />}
          </div>
        )}
      </button>

      {/* Bottom: Title, Artist, Price + ALWAYS VISIBLE buttons */}
      <div className="absolute bottom-0 left-0 right-0 p-2.5 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
        <h3 className={`text-xs font-black text-white truncate leading-tight ${displayError ? 'line-through text-red-300' : ''}`}>
          {track.title}
        </h3>
        <div className="flex items-center justify-between gap-1">
          <p className="text-[10px] text-zinc-300 truncate flex-1">{track.artist}</p>
          {track.genre && (
            <span className="text-[9px] text-orange-300/90 font-bold uppercase tracking-wider truncate max-w-[55%] shrink-0">
              {track.genre}
            </span>
          )}
        </div>

        {/* Always visible: Price + Plays + Credits + Share */}
        <div className="flex items-center justify-between mt-1.5">
          <div className="flex items-center gap-1 min-w-0">
            {track.price > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-yellow-500/25 text-yellow-400 text-[9px] font-bold shrink-0">{track.price.toFixed(2)} EUR</span>
            )}
            <span className="text-[9px] text-zinc-300 font-semibold shrink-0" data-testid={`plays-${track.id}`}>
              {((track.plays || 0) + 1111).toLocaleString('fr-FR')} plays
            </span>
            {isPlaying && <Volume2 size={10} className="text-orange-400 animate-pulse shrink-0" />}
          </div>
          <div className="flex items-center gap-1">
            {track.free_download && (
              <a
                href={`${API}/playlist/tracks/${track.id}/free-download`}
                onClick={(e) => e.stopPropagation()}
                data-testid={`free-download-${track.id}`}
                className="p-1 rounded bg-green-500/30 text-green-300 hover:bg-green-500 hover:text-black transition-colors inline-flex items-center"
                title="Telecharger gratuitement"
                download>
                <Download size={11} />
              </a>
            )}
            {track.price > 0 && (
              <button onClick={(e) => { e.stopPropagation(); onBuy?.(track); }} data-testid={`buy-track-${track.id}`}
                className="p-1 rounded bg-yellow-500/30 text-yellow-300 hover:bg-yellow-500 hover:text-black transition-colors" title={`Acheter ${track.price.toFixed(2)} EUR`}>
                <ShoppingCart size={11} />
              </button>
            )}
            <button onClick={(e) => { e.stopPropagation(); onShowCredits(track); }} data-testid={`credits-track-${track.id}`}
              className="p-1 rounded bg-white/10 text-white/70 hover:bg-white/20 transition-colors" title="Credits">
              <Info size={11} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onShare(track); }} data-testid={`share-track-${track.id}`}
              className="p-1 rounded bg-white/10 text-white/70 hover:bg-orange-500 hover:text-white transition-colors" title="Copier le lien">
              <Link2 size={11} />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        {isPlaying && !displayError && (
          <div className="mt-1.5 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <span className="text-[8px] text-zinc-400 w-5">{formatTime(progress)}</span>
            <div className="flex-1 h-1 bg-white/15 rounded-full cursor-pointer" onClick={seekTo}>
              <div className="h-full bg-orange-500 rounded-full transition-all"
                style={{ width: duration ? `${(progress / duration) * 100}%` : '0%' }} />
            </div>
            <span className="text-[8px] text-zinc-400 w-5">{formatTime(duration)}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ─────────── Credits Modal (with photo) ─────────── */
function CreditsModal({ track, onClose }) {
  const DEFAULT_LOGO = 'https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/ce9n02vn_LOGO%20BLACK.jpg';
  const photoSrc = track.photo ? (track.photo.startsWith('http') ? track.photo : DEFAULT_LOGO) : DEFAULT_LOGO;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9 }}
        className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
        {photoSrc && <img src={photoSrc} alt={track.title} className="w-full h-52 object-cover" />}
        <div className="p-5">
          <h3 className="text-xl font-black text-white mb-0.5">{track.title}</h3>
          <p className="text-sm text-orange-400 font-semibold mb-4">{track.artist}</p>
          <div className="space-y-2.5 text-sm">
            {track.author && <div className="flex justify-between border-b border-zinc-800 pb-1.5"><span className="text-zinc-500">Auteur</span><span className="text-white font-medium">{track.author}</span></div>}
            {track.composer && <div className="flex justify-between border-b border-zinc-800 pb-1.5"><span className="text-zinc-500">Compositeur</span><span className="text-white font-medium">{track.composer}</span></div>}
            {track.genre && <div className="flex justify-between border-b border-zinc-800 pb-1.5"><span className="text-zinc-500">Genre</span><span className="text-white font-medium">{track.genre}</span></div>}
            {track.release_date && <div className="flex justify-between border-b border-zinc-800 pb-1.5"><span className="text-zinc-500">Annee</span><span className="text-white font-medium">{track.release_date.slice(0, 4)}</span></div>}
            {track.duration_seconds > 0 && <div className="flex justify-between border-b border-zinc-800 pb-1.5"><span className="text-zinc-500">Duree</span><span className="text-white font-medium font-mono">{`${Math.floor(track.duration_seconds / 60)}:${Math.floor(track.duration_seconds % 60).toString().padStart(2, '0')}`}</span></div>}
            {track.description && <div className="border-b border-zinc-800 pb-1.5"><span className="text-zinc-500 block mb-1">Description</span><span className="text-white text-xs">{track.description}</span></div>}
            <div className="flex justify-between"><span className="text-zinc-500">Plays</span><span className="text-white font-medium">{((track.plays || 0) + 1111).toLocaleString('fr-FR')}</span></div>
            {track.price > 0 && <div className="flex justify-between"><span className="text-zinc-500">Prix</span><span className="text-yellow-400 font-bold">{track.price.toFixed(2)} EUR</span></div>}
          </div>
          <button onClick={onClose} className="w-full mt-5 py-2.5 bg-zinc-800 text-zinc-300 rounded-lg text-sm font-semibold hover:bg-zinc-700 transition-colors">Fermer</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─────────── Share Toast ─────────── */
function ShareToast({ message, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 2500); return () => clearTimeout(t); }, [onClose]);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-orange-500 text-black font-bold text-sm rounded-full shadow-lg flex items-center gap-2">
      <Check size={16} /> {message}
    </motion.div>
  );
}

/* ─────────── Admin Edit Modal ─────────── */
function TrackEditModal({ track, onClose, onSaved, adminPwd }) {
  const [form, setForm] = useState({
    title: track.title || '', artist: track.artist || '', genre: track.genre || '',
    price: track.price || 0, author: track.author || '', composer: track.composer || '',
    release_date: track.release_date || '', description: track.description || '',
    plays: track.plays || 0, free_download: !!track.free_download
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filesError, setFilesError] = useState('');
  const handleSave = async () => {
    setSaving(true);
    setFilesError('');
    try {
      // 1. Save metadata
      const res = await fetch(`${API}/admin/playlist/tracks/${track.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, plays: parseInt(form.plays) || 0, password: adminPwd })
      });
      if (!res.ok) throw new Error('Echec sauvegarde infos');

      // 2. If new audio and/or photo file → use the new combined endpoint
      if (audioFile || photoFile) {
        const fd = new FormData();
        fd.append('admin_password', adminPwd);
        if (audioFile) fd.append('audio_file', audioFile);
        if (photoFile) fd.append('photo_file', photoFile);
        const fr = await fetch(`${API}/admin/playlist/tracks/${track.id}/files`, { method: 'PUT', body: fd });
        if (!fr.ok) {
          const d = await fr.json().catch(() => ({}));
          setFilesError(d.detail || `Erreur fichier (${fr.status})`);
          setSaving(false);
          return;
        }
      }
      onSaved(); onClose();
    } catch (e) {
      setFilesError(e.message || 'Erreur reseau');
    }
    setSaving(false);
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
        className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 shrink-0">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Edit3 size={18} className="text-orange-400" /> Editer</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="px-6 py-4 space-y-3 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-3">
            {[['Titre *', 'title', 'edit-title'], ['Artiste *', 'artist', 'edit-artist']].map(([label, key, tid]) => (
              <div key={key}><label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">{label}</label>
                <input data-testid={tid} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none" /></div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[['Auteur', 'author', 'edit-author'], ['Compositeur', 'composer', 'edit-composer']].map(([label, key, tid]) => (
              <div key={key}><label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">{label}</label>
                <input data-testid={tid} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none" /></div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Genre</label>
              <input value={form.genre} onChange={e => setForm(p => ({ ...p, genre: e.target.value }))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none" /></div>
            <div><label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Prix EUR</label>
              <input type="number" step="0.01" min="0" value={form.price} onChange={e => setForm(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none" /></div>
            <div><label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Annee</label>
              <input type="number" min="1900" max="2100" placeholder="2025" data-testid="edit-release-date" value={form.release_date} onChange={e => setForm(p => ({ ...p, release_date: e.target.value }))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none" /></div>
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">
              Plays (ecoutes) — clique le bouton pour ajouter +1111 a chaque fois
            </label>
            <div className="flex items-center gap-2">
              <input type="number" min="0" step="1" value={form.plays}
                onChange={e => setForm(p => ({ ...p, plays: e.target.value }))}
                data-testid="edit-plays"
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none" />
              <button type="button"
                onClick={() => setForm(p => ({ ...p, plays: (parseInt(p.plays, 10) || 0) + 1111 }))}
                data-testid="edit-plays-boost"
                title="Ajoute +1111 plays a chaque clic"
                className="px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/30 rounded-lg text-xs font-bold">
                +1111 plays
              </button>
            </div>
          </div>
          <div><label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Description</label>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none resize-none" /></div>
          <label className="flex items-center gap-3 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg cursor-pointer hover:border-green-500/50 transition-colors"
            data-testid="edit-free-download-row">
            <input type="checkbox" checked={form.free_download}
              onChange={e => setForm(p => ({ ...p, free_download: e.target.checked }))}
              data-testid="edit-free-download"
              className="w-4 h-4 accent-green-500" />
            <div>
              <span className="text-sm text-white font-semibold flex items-center gap-1.5"><Download size={13} className="text-green-400" /> Telechargement gratuit</span>
              <span className="text-[10px] text-zinc-500">Si coche, un bouton Download vert apparait sur la carte (meme si la track a un prix)</span>
            </div>
          </label>
          <div><label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Photo couverture</label>
            <div className="flex items-center gap-3">
              {(track.photo || photoFile) && <img src={photoFile ? URL.createObjectURL(photoFile) : (track.photo.startsWith('http') ? track.photo : `${API}/uploads/${track.photo}`)} alt="" className="w-16 h-16 rounded-lg object-cover border border-zinc-700" />}
              <label className="flex-1 cursor-pointer"><div className="flex items-center gap-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-400 text-sm hover:border-orange-500 transition-colors">
                <Image size={14} /> {photoFile ? photoFile.name : 'Choisir une nouvelle photo...'}</div>
                <input type="file" accept="image/*" className="hidden"
                  data-testid="edit-photo-input"
                  onChange={e => setPhotoFile(e.target.files?.[0] || null)} /></label>
            </div>
          </div>
          <div><label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">
            Remplacer le fichier audio MP3/WAV (optionnel — max 25 MB)
          </label>
            <div className="flex items-center gap-3">
              <label className="flex-1 cursor-pointer"><div className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${audioFile ? 'bg-orange-500/20 border-orange-500/50 text-orange-300' : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-orange-500'}`}>
                <Music size={14} />
                <span className="truncate">{audioFile ? `${audioFile.name} (${(audioFile.size / 1024 / 1024).toFixed(1)} MB)` : 'Choisir un nouveau MP3/WAV...'}</span>
              </div>
                <input type="file" accept=".mp3,.wav,.ogg,.flac,.m4a,.aac" className="hidden"
                  data-testid="edit-audio-input"
                  onChange={e => {
                    const f = e.target.files?.[0] || null;
                    setFilesError('');
                    if (f && f.size > 25 * 1024 * 1024) {
                      setFilesError(`Fichier trop lourd (${(f.size / 1024 / 1024).toFixed(1)} MB). Max : 25 MB.`);
                      setAudioFile(null);
                      e.target.value = '';
                      return;
                    }
                    setAudioFile(f);
                  }} /></label>
              {audioFile && <button onClick={() => setAudioFile(null)} className="text-zinc-400 hover:text-red-400 p-1"><X size={14} /></button>}
            </div>
          </div>
          {filesError && (
            <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-300 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {filesError}
            </div>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-zinc-800 bg-zinc-900 shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 bg-zinc-800 text-zinc-400 rounded-lg text-sm font-semibold hover:bg-zinc-700">Annuler</button>
          <button onClick={handleSave} disabled={saving} data-testid="edit-save-button"
            className="flex-1 py-2.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold rounded-lg text-sm disabled:opacity-40 flex items-center justify-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Sauvegarder</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─────────── Admin Upload Panel ─────────── */
const MAX_AUDIO_MB = 25;
function AdminUploadPanel({ onUploaded, isAdmin, onLogin, adminPwd }) {
  const [adminCode, setAdminCode] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [formData, setFormData] = useState({ title: '', artist: 'DJHALLMANN', genre: '', price: 0, description: '', author: '', composer: '', release_date: '', free_download: false });
  const [selectedFile, setSelectedFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const handleLogin = () => { if (adminCode === 'kazajah2024') { onLogin(adminCode); setAdminCode(''); } };

  const handleAudioPick = (file) => {
    setUploadError('');
    if (!file) return;
    const sizeMB = file.size / 1024 / 1024;
    if (sizeMB > MAX_AUDIO_MB) {
      setUploadError(`Fichier trop lourd (${sizeMB.toFixed(1)} MB). Maximum : ${MAX_AUDIO_MB} MB. Compresse ton MP3 (192 kbps suffit).`);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || uploading) return;
    setUploading(true);
    setUploadError('');
    try {
      const fd = new FormData();
      fd.append('admin_password', adminPwd || 'kazajah2024');
      fd.append('title', formData.title);
      fd.append('artist', formData.artist);
      fd.append('genre', formData.genre);
      fd.append('price', formData.price);
      fd.append('description', formData.description);
      fd.append('free_download', formData.free_download ? 'true' : 'false');
      fd.append('author', formData.author);
      fd.append('composer', formData.composer);
      fd.append('release_date', formData.release_date);
      fd.append('audio_file', selectedFile);
      if (photoFile) fd.append('photo_file', photoFile);
      const res = await fetch(`${API}/playlist/upload`, { method: 'POST', body: fd });
      if (!res.ok) {
        let detail = `Erreur ${res.status}`;
        try {
          const d = await res.json();
          if (d.detail) detail = d.detail;
        } catch {
          if (res.status === 413) detail = `Fichier trop lourd. Maximum : ${MAX_AUDIO_MB} MB.`;
          else if (res.status === 504 || res.status === 502) detail = 'Timeout reseau — reessaie ou compresse le fichier.';
        }
        throw new Error(detail);
      }
      setSuccess(true);
      setFormData({ title: '', artist: 'DJHALLMANN', genre: '', price: 0, description: '', author: '', composer: '', release_date: '' });
      setSelectedFile(null);
      setPhotoFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (photoInputRef.current) photoInputRef.current.value = '';
      setTimeout(() => setSuccess(false), 3000);
      onUploaded();
    } catch (err) {
      setUploadError(err.message || 'Erreur reseau');
    }
    setUploading(false);
  };
  if (!isAdmin) return (
    <div className="p-4 rounded-xl bg-zinc-900/40 border border-zinc-800"><div className="flex items-center gap-2">
      <Lock size={14} className="text-zinc-500" />
      <input type="password" value={adminCode} onChange={e => setAdminCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()}
        placeholder="Code admin" data-testid="admin-password-input" className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs focus:border-orange-500 focus:outline-none" />
      <button onClick={handleLogin} data-testid="admin-login-button" className="px-3 py-2 bg-zinc-800 text-zinc-400 rounded-lg text-xs hover:bg-zinc-700">Connexion</button>
    </div></div>
  );
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl bg-gradient-to-br from-orange-500/5 to-pink-500/5 border border-orange-500/30 overflow-hidden">
      <button onClick={() => setShowForm(!showForm)} data-testid="toggle-upload-form" className="w-full p-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors">
        <h3 className="text-sm font-bold text-white flex items-center gap-2"><Upload size={14} className="text-orange-400" /> Upload</h3>
        {showForm ? <ChevronUp size={16} className="text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-400" />}
      </button>
      <AnimatePresence>{showForm && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
          <div className="px-4 pb-4">
            {success && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-2 rounded-lg bg-green-500/20 border border-green-500/40 mb-2 flex items-center gap-2 text-green-400 text-xs font-semibold"><Check size={14} /> Upload OK !</motion.div>}
            <form onSubmit={handleUpload} className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input required value={formData.title} onChange={e => setFormData(p => ({ ...p, title: e.target.value }))} placeholder="Titre *" data-testid="upload-title" className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs placeholder-zinc-500 focus:border-orange-500 focus:outline-none" />
                <input required value={formData.artist} onChange={e => setFormData(p => ({ ...p, artist: e.target.value }))} placeholder="Artiste *" data-testid="upload-artist" className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs placeholder-zinc-500 focus:border-orange-500 focus:outline-none" />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <input value={formData.author} onChange={e => setFormData(p => ({ ...p, author: e.target.value }))} placeholder="Auteur" data-testid="upload-author" className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs placeholder-zinc-500 focus:border-orange-500 focus:outline-none" />
                <input value={formData.composer} onChange={e => setFormData(p => ({ ...p, composer: e.target.value }))} placeholder="Compositeur" data-testid="upload-composer" className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs placeholder-zinc-500 focus:border-orange-500 focus:outline-none" />
                <input value={formData.genre} onChange={e => setFormData(p => ({ ...p, genre: e.target.value }))} placeholder="Genre" className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs placeholder-zinc-500 focus:border-orange-500 focus:outline-none" />
                <input type="number" step="0.01" min="0" value={formData.price} onChange={e => setFormData(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} placeholder="Prix" data-testid="upload-price" className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs placeholder-zinc-500 focus:border-orange-500 focus:outline-none" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="relative"><input ref={fileInputRef} type="file" accept=".mp3,.wav,.ogg,.flac,.m4a,.aac" onChange={e => handleAudioPick(e.target.files?.[0] || null)} data-testid="upload-file-input" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                  <div className={`flex items-center justify-center gap-1 rounded-lg border text-xs px-2 py-2 ${selectedFile ? 'bg-orange-500/20 border-orange-500/50 text-orange-400' : 'bg-zinc-900 border-zinc-700 text-zinc-500'}`}>
                    <Music size={12} />
                    <span className="truncate">{selectedFile ? `${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)` : `Audio * (max ${MAX_AUDIO_MB} MB)`}</span>
                  </div></div>
                <div className="relative"><input ref={photoInputRef} type="file" accept="image/*" onChange={e => setPhotoFile(e.target.files?.[0] || null)} data-testid="upload-photo-input" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                  <div className={`flex items-center justify-center gap-1 rounded-lg border text-xs px-2 py-2 ${photoFile ? 'bg-pink-500/20 border-pink-500/50 text-pink-400' : 'bg-zinc-900 border-zinc-700 text-zinc-500'}`}><Image size={12} /><span className="truncate">{photoFile ? photoFile.name : 'Photo'}</span></div></div>
                <input type="number" min="1900" max="2100" placeholder="2025" value={formData.release_date} onChange={e => setFormData(p => ({ ...p, release_date: e.target.value }))} data-testid="upload-release-date" className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs placeholder-zinc-500 focus:border-orange-500 focus:outline-none" />
              </div>
              <label className="flex items-center gap-2.5 p-2 bg-zinc-900/60 border border-zinc-700 rounded-lg cursor-pointer hover:border-green-500/50 transition-colors"
                data-testid="upload-free-download-row">
                <input type="checkbox" checked={formData.free_download}
                  onChange={e => setFormData(p => ({ ...p, free_download: e.target.checked }))}
                  data-testid="upload-free-download"
                  className="w-4 h-4 accent-green-500" />
                <span className="text-xs text-zinc-300 flex items-center gap-1.5"><Download size={12} className="text-green-400" /> Autoriser le telechargement gratuit (bouton Download visible sur la carte)</span>
              </label>
              {uploadError && (
                <div className="p-2 rounded-lg bg-red-500/15 border border-red-500/40 flex items-start gap-2 text-red-300 text-xs"
                  data-testid="upload-error">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span className="flex-1">{uploadError}</span>
                  <button type="button" onClick={() => setUploadError('')} className="text-red-300/60 hover:text-red-300"><X size={12} /></button>
                </div>
              )}
              <button type="submit" disabled={uploading || !selectedFile || !formData.title} data-testid="upload-submit-button"
                className="w-full py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold rounded-lg disabled:opacity-40 flex items-center justify-center gap-2 text-xs">
                {uploading ? <><Loader2 size={14} className="animate-spin" /> Upload en cours, ne ferme pas la page...</> : <><Upload size={14} /> Uploader</>}</button>
            </form>
          </div>
        </motion.div>
      )}</AnimatePresence>
    </motion.div>
  );
}

/* ─────────── Now Playing Bar ─────────── */
function NowPlayingBar({ track, trackNumber, totalTracks, onNext, onPrev, isPlaying, onToggle }) {
  if (!track) return null;
  const DEFAULT_LOGO = 'https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/ce9n02vn_LOGO%20BLACK.jpg';
  const photoSrc = track.photo ? (track.photo.startsWith('http') ? track.photo : DEFAULT_LOGO) : DEFAULT_LOGO;
  return (
    <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
      className="fixed bottom-0 left-0 right-0 z-40 bg-zinc-950/95 backdrop-blur-lg border-t border-orange-500/30 px-4 py-3">
      <div className="max-w-7xl mx-auto flex items-center gap-3">
        {photoSrc ? <img src={photoSrc} alt="" className="w-10 h-10 rounded-lg object-cover" /> :
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center"><Music size={16} className="text-white" /></div>}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white truncate">{track.title}</p>
          <p className="text-[10px] text-zinc-400 truncate">{track.artist} | #{trackNumber}/{totalTracks}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onPrev} data-testid="now-playing-prev" className="p-2 text-zinc-400 hover:text-white"><SkipBack size={18} /></button>
          <button onClick={onToggle} data-testid="now-playing-toggle" className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white hover:scale-105 transition-transform">
            {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}</button>
          <button onClick={onNext} data-testid="now-playing-next" className="p-2 text-zinc-400 hover:text-white"><SkipForward size={18} /></button>
        </div>
      </div>
    </motion.div>
  );
}

/* ─────────── Main Page ─────────── */
export default function PlaylistPage({ onBack, onPlayGlobal, globalTrackId }) {
  const { isAdmin, adminPwd, login: loginAdmin, logout: logoutAdmin } = useInlineAdmin();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [currentPlayingId, setCurrentPlayingId] = useState(null);
  const [paypalTrack, setPaypalTrack] = useState(null);
  const [editingTrack, setEditingTrack] = useState(null);
  const [creditsTrack, setCreditsTrack] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [errorTrackIds, setErrorTrackIds] = useState(new Set());
  const [shareToast, setShareToast] = useState('');
  const [draggedId, setDraggedId] = useState(null);
  // Search & advanced filters
  const [searchText, setSearchText] = useState('');
  const [genreFilter, setGenreFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all'); // all | free | paid
  const [sortBy, setSortBy] = useState('default'); // default | plays_desc | price_asc | price_desc | recent
  const [showFilters, setShowFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(30); // progressive rendering — start with 30 cards
  const initialTrackRef = useRef(null);
  const hasAutoPlayed = useRef(false);

  useEffect(() => { const p = new URLSearchParams(window.location.search).get('track'); if (p) initialTrackRef.current = p; }, []);

  const loadTracks = useCallback(async () => {
    try { const res = await fetch(`${API}/playlist/tracks`); const data = await res.json(); setTracks(data); return data; } catch { /* */ }
    setLoading(false); return [];
  }, []);

  useEffect(() => {
    loadTracks().then(data => {
      setLoading(false);
      if (initialTrackRef.current && !hasAutoPlayed.current && data.length > 0) {
        const idx = data.findIndex(x => x.id === initialTrackRef.current);
        const t = idx >= 0 ? data[idx] : null;
        if (t) {
          hasAutoPlayed.current = true;
          // Make sure the deep-linked card is in the rendered window so the
          // scroll/highlight actually targets a real DOM node.
          if (idx >= 30) setVisibleCount(idx + 10);
          // Use the GLOBAL mini-player when available (deep-link from a shared
          // URL must trigger the same player as a normal click, otherwise the
          // browser blocks autoplay because the local <audio> isn't visible).
          if (onPlayGlobal) {
            onPlayGlobal(t, data);
          } else {
            setCurrentPlayingId(t.id);
          }
          setTimeout(() => document.getElementById(`track-${t.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 500);
        }
      }
    });
  }, [loadTracks, onPlayGlobal]);

  const uploadedTracks = tracks.filter(t => t.source === 'upload');
  const aiTracks = tracks.filter(t => t.source === 'ai');

  // Available genres (computed from current data)
  const availableGenres = Array.from(new Set(tracks.map(t => (t.genre || '').trim()).filter(Boolean))).sort();

  // Reset progressive rendering when the user changes filters
  useEffect(() => { setVisibleCount(30); }, [activeFilter, searchText, genreFilter, priceFilter, sortBy]);

  const filteredTracks = (() => {
    // 1. source filter (existing pills)
    let list = activeFilter === 'all' ? tracks : activeFilter === 'upload' ? uploadedTracks : aiTracks;
    // 2. text search across title/artist/author/composer/genre
    const q = searchText.trim().toLowerCase();
    if (q) {
      list = list.filter(t => [t.title, t.artist, t.author, t.composer, t.genre]
        .some(x => (x || '').toLowerCase().includes(q)));
    }
    // 3. genre
    if (genreFilter !== 'all') list = list.filter(t => (t.genre || '').toLowerCase() === genreFilter.toLowerCase());
    // 4. price
    if (priceFilter === 'free') list = list.filter(t => !t.price || t.price === 0);
    else if (priceFilter === 'paid') list = list.filter(t => (t.price || 0) > 0);
    // 5. sort
    if (sortBy === 'plays_desc') list = [...list].sort((a, b) => (b.plays || 0) - (a.plays || 0));
    else if (sortBy === 'price_asc') list = [...list].sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sortBy === 'price_desc') list = [...list].sort((a, b) => (b.price || 0) - (a.price || 0));
    else if (sortBy === 'recent') list = [...list].sort((a, b) => (b.release_date || '').localeCompare(a.release_date || ''));
    return list;
  })();

  // Highlight the track that is actually playing (global mini-player wins over local state)
  const playingId = globalTrackId || currentPlayingId;
  const currentPlayingIndex = filteredTracks.findIndex(t => t.id === playingId);
  const currentTrack = currentPlayingIndex >= 0 ? filteredTracks[currentPlayingIndex] : null;

  const handleTrackEnded = useCallback(() => {
    setCurrentPlayingId(prev => {
      const idx = filteredTracks.findIndex(t => t.id === prev);
      for (let i = 1; i <= filteredTracks.length; i++) {
        const ni = (idx + i) % filteredTracks.length;
        if (!errorTrackIds.has(filteredTracks[ni].id)) return filteredTracks[ni].id;
      } return null;
    });
  }, [filteredTracks, errorTrackIds]);

  const toggleTrack = (id) => {
    const newId = currentPlayingId === id ? null : id;
    setCurrentPlayingId(newId);
    // Use global player only - stop local audio
    if (onPlayGlobal && newId) {
      const track = filteredTracks.find(t => t.id === id);
      if (track) onPlayGlobal(track);
    } else if (onPlayGlobal && !newId) {
      onPlayGlobal(null);
    }
  };
  const playNext = () => { if (currentPlayingIndex >= 0) setCurrentPlayingId(filteredTracks[(currentPlayingIndex + 1) % filteredTracks.length].id); };
  const playPrev = () => { if (currentPlayingIndex >= 0) setCurrentPlayingId(filteredTracks[(currentPlayingIndex - 1 + filteredTracks.length) % filteredTracks.length].id); };
  const handleTrackError = useCallback((id) => setErrorTrackIds(prev => new Set([...prev, id])), []);

  const handleShare = (track) => {
    const url = `${SITE_URL}/playlist?track=${track.id}`;
    navigator.clipboard.writeText(url).then(() => setShareToast(`Lien copie: ${track.title}`)).catch(() => {
      const inp = document.createElement('input'); inp.value = url; document.body.appendChild(inp); inp.select(); document.execCommand('copy'); document.body.removeChild(inp);
      setShareToast(`Lien copie: ${track.title}`);
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette track ?')) return;
    setDeletingId(id);
    try {
      const r = await fetch(`${API}/playlist/tracks/${id}?admin_password=${encodeURIComponent(adminPwd)}`, { method: 'DELETE' });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(`Erreur suppression: ${d.detail || r.status}`);
      } else {
        if (currentPlayingId === id) setCurrentPlayingId(null);
        await loadTracks();
      }
    } catch (e) {
      alert(`Erreur reseau: ${e.message || ''}`);
    }
    setDeletingId(null);
  };

  const handleDownload = (track) => {
    if (!track?.id) return;
    const url = `${API}/admin/playlist/tracks/${track.id}/download?admin_password=${encodeURIComponent(adminPwd)}`;
    // Use a hidden anchor with download attribute to force browser save dialog
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.target = '_self';
    a.download = `${track.artist || 'KAZAJAH'} - ${track.title || 'track'}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /* ── Drag & Drop reorder (admin only) ── */
  const handleDragStart = (e, id) => { setDraggedId(id); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) { setDraggedId(null); return; }
    const ids = filteredTracks.map(t => t.id);
    const fromIdx = ids.indexOf(draggedId);
    const toIdx = ids.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) { setDraggedId(null); return; }
    // Reorder locally
    const newIds = [...ids];
    newIds.splice(fromIdx, 1);
    newIds.splice(toIdx, 0, draggedId);
    // Optimistic UI update
    const reordered = newIds.map(id => tracks.find(t => t.id === id)).filter(Boolean);
    setTracks(reordered);
    setDraggedId(null);
    // Save to backend
    try {
      await fetch(`${API}/admin/playlist/reorder`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPwd, track_ids: newIds })
      });
    } catch { /* reload if fails */ await loadTracks(); }
  };

  const getTrackNumber = (track, list) => list.findIndex(t => t.id === track.id) + 1;

  // Top 5 most played tracks (used in the leaderboard at the top of the page).
  // We inflate visible counts by +1111 per track for social proof / launch boost.
  const VISITS_BOOST = 1111;
  const displayVisits = (n) => ((n || 0) + VISITS_BOOST);
  const top5 = [...tracks]
    .sort((a, b) => (b.plays || 0) - (a.plays || 0))
    .slice(0, 5);
  const totalPlays = tracks.reduce((s, t) => s + displayVisits(t.plays), 0);
  const formatTotalPlays = (n) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
    return String(n);
  };

  return (
    <div className="min-h-screen bg-black text-white pt-28 pb-32" data-testid="playlist-page">
      <div className="px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
          <button onClick={onBack} data-testid="playlist-back-button" className="text-zinc-400 hover:text-white transition-colors mb-3 inline-flex items-center gap-2 text-sm">
            <ArrowRight className="rotate-180" size={16} /> Retour</button>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center"><Radio size={20} className="text-white" /></div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-black"><span className="bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">KAZAJAH RADIO</span></h1>
              <p className="text-zinc-500 text-xs flex items-center gap-1"><Disc3 size={12} className="text-orange-400 animate-spin" style={{ animationDuration: '3s' }} /> Autoplay — {tracks.length} tracks</p>
            </div>
          </div>
        </motion.div>

        {/* TOP 5 — Hits du label par nombre de plays */}
        {top5.length > 0 && top5[0].plays > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="mb-5 p-4 rounded-2xl bg-gradient-to-br from-yellow-500/10 via-orange-500/5 to-pink-500/10 border border-yellow-500/20"
            data-testid="top5-section">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="text-sm font-black uppercase tracking-widest text-yellow-300 flex items-center gap-2">
                <Sparkles size={14} className="text-yellow-400" /> Top 5 du label
              </h2>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider">
                <span className="text-zinc-500">Plus écoutés</span>
                {totalPlays > 0 && (
                  <span className="px-2 py-1 rounded-full bg-yellow-500/20 border border-yellow-500/30 text-yellow-300 font-bold normal-case tracking-normal" data-testid="total-plays-counter">
                    🎧 +{formatTotalPlays(totalPlays)} plays sur le label
                  </span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {top5.map((t, i) => {
                const isOn = playingId === t.id;
                const photoSrc = t.photo
                  ? (t.photo.startsWith('http') ? t.photo : 'https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/ce9n02vn_LOGO%20BLACK.jpg')
                  : 'https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/ce9n02vn_LOGO%20BLACK.jpg';
                return (
                  <button key={t.id} onClick={() => toggleTrack(t.id)}
                    data-testid={`top5-track-${t.id}`}
                    className={`group relative flex items-center gap-2 p-2 rounded-xl border transition-all text-left ${
                      isOn
                        ? 'bg-orange-500/15 border-orange-500/60 shadow-lg shadow-orange-500/20'
                        : 'bg-zinc-900/60 border-zinc-800 hover:border-yellow-500/40'
                    }`}>
                    <div className="relative shrink-0">
                      <img src={photoSrc} alt={t.title}
                        className="w-12 h-12 rounded-lg object-cover" />
                      <span className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 text-black text-[10px] font-black flex items-center justify-center shadow-md">
                        {i + 1}
                      </span>
                      {isOn && (
                        <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center animate-pulse">
                          <Volume2 size={10} className="text-white" />
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-bold truncate ${isOn ? 'text-orange-300' : 'text-white'}`}>{t.title}</p>
                      <p className="text-[10px] text-zinc-400 truncate">{t.artist}</p>
                      <p className="text-[10px] text-yellow-400 font-bold mt-0.5">
                        {displayVisits(t.plays).toLocaleString('fr-FR')} plays
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        <div className="flex gap-2 mb-3 flex-wrap items-center">
          {[{ id: 'all', label: `Tous (${tracks.length})` }, { id: 'upload', label: `Radio (${uploadedTracks.length})` }, { id: 'ai', label: `AI (${aiTracks.length})` }].map(f => (
            <button key={f.id} onClick={() => setActiveFilter(f.id)} data-testid={`filter-${f.id}`}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${activeFilter === f.id ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>{f.label}</button>
          ))}
          <button onClick={() => setShowFilters(s => !s)} data-testid="toggle-search"
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all inline-flex items-center gap-1.5 ${showFilters ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
            <Search size={11} /> Recherche
          </button>
          {isAdmin && <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-green-500/20 text-green-400 border border-green-500/30">Admin (glisser pour reordonner)</span>}
          {isAdmin && (
            <button onClick={() => { if (window.confirm('Se deconnecter de l\'admin ?')) logoutAdmin(); }}
              data-testid="admin-logout-btn"
              className="px-3 py-1.5 rounded-full text-xs font-semibold bg-zinc-800 text-zinc-400 hover:bg-zinc-700 transition-colors">
              Se deconnecter
            </button>
          )}
          {isAdmin && (
            <button onClick={async () => {
                if (!window.confirm('Ajouter +1111 plays a TOUTES les tracks radio ?')) return;
                try {
                  const r = await fetch(`${API}/admin/playlist/boost-plays`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ password: adminPwd, delta: 1111 })
                  });
                  const d = await r.json();
                  if (r.ok) {
                    alert(`+1111 plays ajoutes sur ${d.updated} tracks.`);
                    loadTracks();
                  } else { alert(d.detail || 'Erreur'); }
                } catch (e) { alert('Erreur reseau'); }
              }}
              data-testid="boost-plays-all-btn"
              title="Ajoute +1111 plays a toutes les tracks radio en un clic"
              className="px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 transition-colors inline-flex items-center gap-1">
              <Sparkles size={11} /> +1111 plays a toutes
            </button>
          )}
        </div>

        {/* Advanced search & filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mb-4">
              <div className="p-3 rounded-xl bg-zinc-900/50 border border-zinc-800 space-y-2"
                data-testid="advanced-filters">
                <div className="relative">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input value={searchText} onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Rechercher titre, artiste, auteur, compositeur, genre..."
                    data-testid="playlist-search-input"
                    className="w-full pl-9 pr-3 py-2 bg-zinc-950 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-orange-500" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <select value={genreFilter} onChange={(e) => setGenreFilter(e.target.value)}
                    data-testid="filter-genre"
                    className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-lg text-white text-xs focus:outline-none focus:border-orange-500">
                    <option value="all">Tous les genres</option>
                    {availableGenres.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                  <select value={priceFilter} onChange={(e) => setPriceFilter(e.target.value)}
                    data-testid="filter-price"
                    className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-lg text-white text-xs focus:outline-none focus:border-orange-500">
                    <option value="all">Tous les prix</option>
                    <option value="free">Gratuit uniquement</option>
                    <option value="paid">Payant uniquement</option>
                  </select>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                    data-testid="filter-sort"
                    className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-lg text-white text-xs focus:outline-none focus:border-orange-500">
                    <option value="default">Tri par defaut</option>
                    <option value="plays_desc">+ Plays</option>
                    <option value="price_asc">Prix croissant</option>
                    <option value="price_desc">Prix decroissant</option>
                    <option value="recent">Plus recents</option>
                  </select>
                </div>
                {(searchText || genreFilter !== 'all' || priceFilter !== 'all' || sortBy !== 'default') && (
                  <div className="flex items-center justify-between text-xs text-zinc-500 pt-1">
                    <span>{filteredTracks.length} resultat{filteredTracks.length > 1 ? 's' : ''}</span>
                    <button onClick={() => { setSearchText(''); setGenreFilter('all'); setPriceFilter('all'); setSortBy('default'); }}
                      className="text-orange-400 hover:text-orange-300 font-semibold">Reset</button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mb-5"><AdminUploadPanel onUploaded={loadTracks} isAdmin={isAdmin} adminPwd={adminPwd} onLogin={(pwd) => loginAdmin(pwd)} /></div>

        {loading ? (
          <div className="text-center py-16"><Loader2 size={32} className="animate-spin text-orange-400 mx-auto mb-3" /><p className="text-zinc-500">Chargement...</p></div>
        ) : filteredTracks.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {filteredTracks.slice(0, visibleCount).map(track => (
                <TrackCard key={track.id} track={track} isPlaying={playingId === track.id}
                  onTogglePlay={() => toggleTrack(track.id)} onEnded={handleTrackEnded}
                  trackNumber={getTrackNumber(track, filteredTracks)} onError={handleTrackError} hasError={errorTrackIds.has(track.id)}
                  onShare={handleShare} isAdmin={isAdmin} onEdit={setEditingTrack} onDelete={handleDelete} onDownload={handleDownload}
                  deleting={deletingId === track.id} onShowCredits={setCreditsTrack}
                  onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} isDragging={draggedId === track.id}
                  useGlobalPlayer={!!onPlayGlobal} onBuy={setPaypalTrack} />
              ))}
            </div>
            {visibleCount < filteredTracks.length && (
              <div className="mt-6 text-center">
                <button onClick={() => setVisibleCount(c => c + 30)}
                  data-testid="load-more-tracks"
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold rounded-full text-sm hover:scale-105 transition-transform shadow-lg shadow-orange-500/30">
                  Voir {Math.min(30, filteredTracks.length - visibleCount)} tracks de plus ({filteredTracks.length - visibleCount} restantes)
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="p-12 rounded-xl border border-dashed border-zinc-700 bg-zinc-900/30 text-center">
            <Radio size={48} className="text-zinc-600 mx-auto mb-4" /><p className="text-zinc-500 font-bold text-lg">La radio est vide</p></div>
        )}

        <div className="mt-12 text-center"><button onClick={onBack} className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 mx-auto"><ArrowRight className="rotate-180" size={18} /> Retour</button></div>
      </div>

      <AnimatePresence>{currentTrack && !onPlayGlobal && <NowPlayingBar track={currentTrack} trackNumber={currentPlayingIndex + 1} totalTracks={filteredTracks.length} onNext={playNext} onPrev={playPrev} isPlaying={!!currentPlayingId} onToggle={() => setCurrentPlayingId(prev => prev ? null : currentTrack.id)} />}</AnimatePresence>
      <AnimatePresence>{creditsTrack && <CreditsModal track={creditsTrack} onClose={() => setCreditsTrack(null)} />}</AnimatePresence>
      <AnimatePresence>{shareToast && <ShareToast message={shareToast} onClose={() => setShareToast('')} />}</AnimatePresence>
      <AnimatePresence>{editingTrack && <TrackEditModal track={editingTrack} adminPwd={adminPwd} onClose={() => setEditingTrack(null)} onSaved={loadTracks} />}</AnimatePresence>
      {paypalTrack && <PayPalCheckoutModal product={{ id: paypalTrack.id, name: paypalTrack.title, price: paypalTrack.price, description: paypalTrack.genre }}
        productType="playlist_track" onClose={() => setPaypalTrack(null)} onSuccess={() => setPaypalTrack(null)} />}
    </div>
  );
}
